import React from "react";
import { FightStackSettings } from "./FightStackSettings";
import { Grid, Box, Button, InputLabel, Typography, Modal, Stack, List, ListItem } from '@mui/material';
import RoiBoostButton from "../Table/RoiBoostButton";

interface OwnProps {
  stackSettings: FightStackSettings[];
  setStackSettings: (stackSettings: FightStackSettings[]) => void;
};

type Props = OwnProps;

const FightStackSettingsAdjuster = (props: Props) => {
  const { stackSettings, setStackSettings } = props;
  const [open, setOpen] = React.useState(false);
  const [totalPercent, setTotalPercent] = React.useState(0);
  const [internalStackSettings, setInternalStackSettings] = React.useState<FightStackSettings[]>([]);

  const handlePercentChange = (constraint: FightStackSettings) => {
    const previousConstraints = [...internalStackSettings];
    const index = previousConstraints.findIndex(x => x.matchupLabel === constraint.matchupLabel);
    previousConstraints[index] = constraint;
    setInternalStackSettings(previousConstraints);
  }

  React.useEffect(() => {
    if (internalStackSettings) {
      const total = internalStackSettings.reduce((partialSum, a) => partialSum + a.percent * 100, 0);
      setTotalPercent(total);
    }
  }, [internalStackSettings])

  React.useEffect(() => {
    setInternalStackSettings(stackSettings);
  }, [stackSettings])

  const handleApply = () => {
    setStackSettings(internalStackSettings);
    setOpen(false);
  }
  // Sync up the internal and full stack settings when the modal is opened
  React.useEffect(() => {
    if (open) {
      setInternalStackSettings(stackSettings);
    }
  }, [open])

  return (
    <>
      <Box>
        <InputLabel
          sx={{
            color: 'white !important',
            opacity: 0.7,
            fontFamily: 'Inter !important',
            fontSize: '12px !important',
            fontStyle: 'normal !important',
            fontHeight: 500,
          }}
        >
          Fight Stack Exposures
        </InputLabel>
        <Button
          sx={{
            width: 200,
            height: 36,
            borderRadius: '6px !important',
            display: 'flex !important',
            padding: '0 12px !important',
            justifyContent: 'space-between !important',
            color: 'white !important',
            textTransform: 'capitalize !important',
            fontFamily: 'Inter !important',
            fontSize: '12px !important',
            fontStyle: 'normal !important',
            fontHeight: 500,
            backgroundColor: '#000A16 !important',
            fontWeight: 400,
          }}
          onClick={() => setOpen(true)}
        >
          Fight Stack Exposures
          <StackSVG />
        </Button>
      </Box>
      <Modal open={open} sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: 'white',
        fontFamily: 'Roboto',
        '& .MuiButton-contained.Mui-disabled': {
          background: '#343434 !important',
        },
        '& .subtitle': {
          fontSize: 14,
          // lineHeight: '12px',
          fontWeight: 400,
          padding: '12px 8px',
        },
        '& .header': {
          backgroundColor: '#031630',
          padding: 0,
          borderTop: '1px solid rgba(251,255,57, .3)',
          borderBottom: '1px solid rgba(251,255,57, .3)',
          '& .headerCell': {
            textAlign: 'center',
            fontSize: 12,
            '&:not(:last-child)': {
              borderRight: '1px solid rgba(251,255,57, .3)',
            },
            padding: '8px 8px',
          }
        },
        '& .row': {
          padding: 0,
          fontWeight: 500,
          '& .cell': {
            display: 'flex',
            alignItems: 'center',
            '&:not(:last-child)': {
              borderRight: '1px solid rgba(251,255,57, .3)',
            },
            padding: '8px 6px',
          }
        },
        '& .total': {
          padding: '0',
          fontWeight: 500,
          borderTop: '1px solid rgba(251,255,57, .3)',
          borderBottom: '1px solid rgba(251,255,57, .3)',
          '& .cell': {
            display: 'flex',
            alignItems: 'center',
            '&:not(:last-child)': {
              borderRight: '1px solid rgba(251,255,57, .3)',
            },
            padding: '8px 6px',
          },
          '& .sum': {
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: 14,
            lineHeight: '150%',
            color: '#31FF39',
          }
        },
        '& .input': {
          color: 'white',
          width: '100%',
          border: '1px solid white',
          borderRadius: 6,
          padding: '2px 12px'
        }
      }} disableAutoFocus onClose={(event: object, reason: string) => {
        if (reason?.toLowerCase() === 'backdropclick') {
          setOpen(false)
        }
      }}>
        <Stack sx={{
          backgroundColor: '#010E20',
          borderRadius: 10,
          minHeight: 390,
          width: 600,
        }}>
          <Box padding="10px 12px">
            <Typography sx={{ color: 'FFF', fontSize: '14px', fontWeight: 700, lineHeight: '140%' }}>
              Fight Stack Exposures
            </Typography>
            <Typography sx={{ color: 'FFF', fontSize: '10px', fontWeight: 400, lineHeight: '120%' }}>
              Adjust fight stack exposures to match your desired exposure.
            </Typography>
          </Box>
          <Box>
            <List sx={{
              maxHeight: 300,
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: '0.2em',
              },
              '&::-webkit-scrollbar-track': {
                boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
                webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'slategrey',
              },
              padding: 0,
            }}>
              <ListItem className='header'>
                <Grid container>
                  <Grid item xs={8} className='headerCell'>Fight Stack</Grid>
                  <Grid item xs={4} className='headerCell'>Desired Exposure</Grid>
                </Grid>
              </ListItem>
            </List>
            <List sx={{
              maxHeight: 300,
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: '0.2em',
              },
              '&::-webkit-scrollbar-track': {
                boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
                webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'slategrey',
              },
              padding: 0,
            }}>
              {
                internalStackSettings.map((x: FightStackSettings, i: number) => (
                  <FightStackListItem onIncrement={handlePercentChange} fightStack={x} totalPercent={totalPercent} key={x.matchupLabel + i} />
                ))
              }
            </List>
            <List>
              <ListItem className='total'>
                <Grid container>
                  <Grid item xs={8} className='cell'>Unstacked</Grid>
                  <Grid item xs={4} className='cell sum' style={{ color: totalPercent > 100 ? '#FF6A7B' : '', }}>{(100 - totalPercent)?.toFixed(0)}%</Grid>
                </Grid>
              </ListItem>
            </List>
          </Box>
          <Box padding="10px 12px" bgcolor='#010E20' display="flex" justifyContent="flex-end">
            <Button variant='contained' sx={{
              backgroundColor: "#015ACE",
              borderRadius: 4,
              width: 85,
              height: 26,
              padding: '0 12px',
              color: 'white',
              '&:hover': {
                backgroundColor: "#015ACE",
              },
            }} size='small' onClick={() => handleApply()} disabled={totalPercent > 100}>Apply</Button>
          </Box>
        </Stack>
      </Modal>
    </>
  )
}

const FightStackListItem = (props: { fightStack: FightStackSettings, onIncrement: (newSetting: FightStackSettings) => void, totalPercent: number }) => {
  const { fightStack, totalPercent, onIncrement } = props;
  const { fighters, matchupLabel, percent } = fightStack;
  return (
    <ListItem className='row'>
      <Grid container>
        <Grid item xs={8} className='cell'>{matchupLabel}</Grid>
        <Grid item xs={4} className='cell total'><RoiBoostButton changeOverride={1} disableMax={!(totalPercent < 100) || percent >= 100} disableMin={percent * 100 <= 0} onChange={(newPercent) => onIncrement({ fighters, matchupLabel, percent: newPercent / 100 })} previousPercent={parseInt((percent * 100).toFixed(0))} /></Grid>
      </Grid>
    </ListItem>
  );
}

const StackSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M1.5 18C1.1 18 0.75 17.85 0.45 17.55C0.15 17.25 0 16.9 0 16.5V1.5C0 1.1 0.15 0.75 0.45 0.45C0.75 0.15 1.1 0 1.5 0H16.5C16.9 0 17.25 0.15 17.55 0.45C17.85 0.75 18 1.1 18 1.5V16.5C18 16.9 17.85 17.25 17.55 17.55C17.25 17.85 16.9 18 16.5 18H1.5ZM1.5 16.5H16.5V1.5L1.5 16.5ZM11.85 15.3V13.1H9.65V11.85H11.85V9.65H13.1V11.85H15.3V13.1H13.1V15.3H11.85ZM2.7 5.05H7.75V3.8H2.7V5.05Z" fill="white" fillOpacity="0.56" />
  </svg>
)

export default FightStackSettingsAdjuster;